import {
  CASHBOOK_IMPORT_MAX_ROWS,
  DEFAULT_CASHBOOK_CATEGORIES,
  type CashbookCategory,
  type CashbookEntryType
} from '@finvox/shared/cashbook';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { commitCashbookImport, dryRunCashbookImport, type CashbookPermissions } from '@/lib/cashbookFirestore';
import { toastError } from '@/lib/toastErrors';
import { useToast } from '@/contexts/ToastContext';

type ImportRow = {
  type: CashbookEntryType;
  amount: string;
  date: string;
  categoryKey: string;
  memo?: string;
};

type DryRunResult = {
  ok: boolean;
  errors: Array<{ index: number; message: string }>;
  warnings: Array<{ index: number; message: string }>;
  rowCount: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  periodId: string;
  uid: string;
  permissions: CashbookPermissions;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onSuccess: () => void;
  customCategories?: CashbookCategory[];
};

function normalizeType(raw: string): CashbookEntryType | null {
  const v = raw.trim().toLowerCase();
  if (v === 'in' || v === 'income') return 'in';
  if (v === 'out' || v === 'expense') return 'out';
  return null;
}

function parseSheetRows(sheet: XLSX.WorkSheet): ImportRow[] {
  const aoa = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, { header: 1, defval: '' });
  const rows: ImportRow[] = [];
  for (let i = 1; i < aoa.length; i++) {
    const line = aoa[i];
    if (!line || line.every((c) => String(c).trim() === '')) continue;
    const date = String(line[0] ?? '').trim();
    const type = normalizeType(String(line[1] ?? ''));
    const amount = String(line[2] ?? '').trim();
    const categoryKey = String(line[3] ?? '').trim();
    const memo = String(line[4] ?? '').trim() || undefined;
    if (!date && !amount) continue;
    if (!type) throw new Error(`Invalid type on row ${i + 1}`);
    rows.push({ date, type, amount, categoryKey, memo });
  }
  return rows;
}

export function downloadImportTemplate(
  customCategories: { key: string; type: string }[] = []
): void {
  const defaults = DEFAULT_CASHBOOK_CATEGORIES.map((c) => [c.key, c.type] as [string, string]);
  const extras = customCategories
    .filter((c) => !DEFAULT_CASHBOOK_CATEGORIES.some((d) => d.key === c.key))
    .map((c) => [c.key, c.type] as [string, string]);
  const categories = [...defaults, ...extras];
  const entries = [
    ['Date', 'Type (in/out)', 'Amount', 'Category', 'Note'],
    ['2026-01-02', 'in', '100.00', 'sales', 'Example sale'],
    ['2026-01-02', 'out', '40.00', 'rent', 'Example rent']
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(entries), 'Entries');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Key', 'Type'], ...categories]), 'Categories');
  XLSX.writeFile(wb, 'cashbook-import-template.xlsx');
}

export function CashbookImportDialog({
  open,
  onClose,
  orgId,
  periodId,
  uid,
  permissions,
  busy,
  setBusy,
  onSuccess,
  customCategories = []
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [ackDuplicates, setAckDuplicates] = useState(false);

  if (!open) return null;

  async function handleFile(file: File) {
    setDryRun(null);
    setAckDuplicates(false);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0] ?? ''];
      if (!sheet) throw new Error(t('cashbook.import.emptyFile'));
      const parsed = parseSheetRows(sheet);
      if (parsed.length === 0) throw new Error(t('cashbook.import.emptyFile'));
      if (parsed.length > CASHBOOK_IMPORT_MAX_ROWS) {
        throw new Error(t('cashbook.import.tooManyRows', { max: CASHBOOK_IMPORT_MAX_ROWS }));
      }
      setRows(parsed);
      setBusy(true);
      const result = await dryRunCashbookImport(
        orgId,
        { orgId, periodId, rows: parsed },
        permissions
      );
      setDryRun(result);
    } catch (e) {
      toastError(toast, e, 'cashbook-import');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    if (!dryRun?.ok) return;
    if (dryRun.warnings.length > 0 && !ackDuplicates) return;
    setBusy(true);
    try {
      const res = await commitCashbookImport(
        orgId,
        uid,
        { orgId, periodId, rows },
        permissions
      );
      toast.success(t('cashbook.import.success', { count: res.imported }), 'cashbook-import');
      onSuccess();
      onClose();
    } catch (e) {
      toastError(toast, e, 'cashbook-import');
    } finally {
      setBusy(false);
    }
  }

  const canCommit =
    dryRun?.ok && (dryRun.warnings.length === 0 || ackDuplicates) && rows.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden p-6 shadow-lg">
        <div className="shrink-0">
          <h2 className="text-lg font-semibold">{t('cashbook.import.title')}</h2>
          <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('cashbook.import.hint')}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => downloadImportTemplate(customCategories)}
            >
              {t('cashbook.import.downloadTemplate')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
              {t('cashbook.import.chooseFile')}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        {dryRun ? (
          <div className="mt-4 min-h-0 flex-1 space-y-3 overflow-y-auto">
            <p className="text-sm">
              {t('cashbook.import.previewCount', { count: dryRun.rowCount })}
            </p>
            {dryRun.errors.length > 0 ? (
              <ul className="max-h-48 overflow-y-auto rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                {dryRun.errors.map((e) => (
                  <li key={`e-${e.index}`}>
                    {t('cashbook.import.rowError', { row: e.index + 2, message: e.message })}
                  </li>
                ))}
              </ul>
            ) : null}
            {dryRun.warnings.length > 0 ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <ul className="max-h-48 overflow-y-auto">
                  {dryRun.warnings.map((w) => (
                    <li key={`w-${w.index}`}>
                      {t('cashbook.import.rowWarning', { row: w.index + 2, message: w.message })}
                    </li>
                  ))}
                </ul>
                <label className="mt-3 flex items-center gap-2">
                  <Checkbox
                    checked={ackDuplicates}
                    onChange={(e) => setAckDuplicates(e.target.checked)}
                  />
                  {t('cashbook.import.ackDuplicates')}
                </label>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex shrink-0 justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button loading={busy} disabled={!canCommit} onClick={() => void handleCommit()}>
            {t('cashbook.import.confirm')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
